import Playlist from '../../models/playlist/playlist';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import Source from '../../models/source';
import Constants from '../../settings';

/**
 * Which playlist "go to the current track" (ctrl+L, ⌘L) should open.
 *
 * iTunes answered with the playlist the song had been started from, and so
 * does this - as long as that playlist still exists and still holds the
 * track. Otherwise it is the Now Playing queue: that is where the track is
 * actually playing from, and the one place it is certain to be. Nothing is
 * guessed. A track that sits in three playlists is never sent to one it was
 * not played from.
 *
 * Undefined when there is nowhere to go - no track, or a queue that no
 * longer holds it - which is the caller's cue to leave the key alone.
 */
export default function playlistToShow(
  track: PlaylistItem,
  /** What the queue was started from, when that was a library playlist. */
  sourcePlaylistId: string | undefined,
  queue: Playlist,
  libraryPlaylist: Playlist | undefined,
  playlists: Playlist[],
): string | undefined {
  if (track.trackType === undefined || track.trackType === Source.Null) {
    return undefined;
  }

  if (sourcePlaylistId !== undefined) {
    const source =
      sourcePlaylistId === Constants.LibraryId
        ? libraryPlaylist
        : sourcePlaylistId === Constants.ActivePlaylistId
          ? queue
          : playlists.find((x) => x.id === sourcePlaylistId);

    if (source && indexOfPlaylistItem(source.tracks, track) !== -1) {
      return sourcePlaylistId;
    }
  }

  return indexOfPlaylistItem(queue.tracks, track) !== -1 ? Constants.ActivePlaylistId : undefined;
}

/**
 * Where a queue entry is in a list of playlist items.
 *
 * The same object first: a playlist that holds one track twice has two
 * entries, and when the queue was built from this very list (or is this
 * list) the one that is playing is known exactly. Failing that - the queue
 * came back from localStorage, the playlist was rebuilt by an edit - the
 * first entry for the same track.
 */
export function indexOfPlaylistItem(items: PlaylistItem[], wanted: PlaylistItem) {
  const sameObject = items.indexOf(wanted);

  if (sameObject !== -1) {
    return sameObject;
  }

  return items.findIndex((x) => x.id === wanted.id && x.trackType === wanted.trackType);
}
